import React, { useCallback, useEffect, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import {
  getServerS3StorageSummary,
  ServerS3StorageSummary as ServerS3StorageSummaryType,
  startServerS3StorageScan,
} from '../../../api/s3StorageAuditApi';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import Modal from '../../modules/Modal';
import { formatStorageBytes, formatStorageDate } from './utils';
import styles from './ServerS3StorageSummary.module.scss';

const ACTIVE_STATUSES = ['PENDING', 'SCANNING'];

const ServerS3StorageSummary = () => {
  const [summary, setSummary] = useState<ServerS3StorageSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const summaryRequestRef = useRef(0);
  const scanning = ACTIVE_STATUSES.includes(summary?.status || '');
  const prefixOnly = summary?.actual_scope !== 'ALL_BUCKET_PREFIXES';
  const sharedScan = summary?.shared_reference_scan;
  const breakdownAvailable = Array.isArray(summary?.prefix_breakdown);
  const registeredPrefixes = new Set(
    (sharedScan?.servers || []).map((server) => `${server.server_prefix}/`),
  );
  const prefixBreakdown = [...(summary?.prefix_breakdown || [])].sort(
    (left, right) =>
      Number(registeredPrefixes.has(right.prefix)) -
      Number(registeredPrefixes.has(left.prefix)),
  );
  const waitingForSharedScan = Boolean(
    sharedScan?.round_id && !sharedScan.complete && !sharedScan.error,
  );
  const statusText = loadError
    ? 'Unavailable'
    : summary?.scan_error
    ? 'Scan failed'
    : sharedScan?.error
    ? `${summary?.server_prefix || 'Prefix unavailable'} · Shared scan unavailable`
    : sharedScan
    ? `${summary?.server_prefix || 'Prefix unavailable'} · Shared scan ${sharedScan.servers_scanned}/${sharedScan.servers_total}`
    : summary?.server_prefix || 'Prefix unavailable';
  const tooltipError = summary?.scan_error || sharedScan?.error || loadError;

  const loadSummary = useCallback(async (silent = false) => {
    const requestId = ++summaryRequestRef.current;
    if (!silent) setLoading(true);
    try {
      const response = await getServerS3StorageSummary();
      if (requestId !== summaryRequestRef.current) return;
      setSummary(response.data);
      setLoadError(null);
    } catch (error: any) {
      if (requestId !== summaryRequestRef.current) return;
      if (!silent) {
        const message = error?.response?.data?.message || 'Failed to load server S3 storage';
        setLoadError(message);
        enqueueSnackbar(message, { variant: 'error' });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    return () => {
      summaryRequestRef.current += 1;
    };
  }, [loadSummary]);

  useEffect(() => {
    if (requesting || (!scanning && !waitingForSharedScan)) return;
    let cancelled = false;
    let timeout: number;
    const stopAt = scanning ? null : Date.now() + 60 * 60 * 1000;
    const poll = async () => {
      await loadSummary(true);
      if (!cancelled && (!stopAt || Date.now() < stopAt)) {
        timeout = window.setTimeout(poll, 5000);
      }
    };
    timeout = window.setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [requesting, scanning, waitingForSharedScan, loadSummary]);

  const startScan = async () => {
    const requestId = ++summaryRequestRef.current;
    setRequesting(true);
    try {
      const response = await startServerS3StorageScan();
      if (requestId === summaryRequestRef.current) setSummary(response.data);
      setConfirmOpen(false);
      enqueueSnackbar('Server S3 and database reference scan started', {
        variant: 'success',
      });
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Failed to start server S3 storage scan', { variant: 'error' });
    } finally {
      setRequesting(false);
    }
  };

  const prefixTooltip = (
    <div className={styles.prefixTooltip}>
      <div className={styles.prefixHeader}>
        <span>Storage by root prefix</span>
        <strong>{formatStorageBytes(summary?.total_size_bytes)}</strong>
      </div>
      {loading ? (
        <div className={styles.prefixMessage}>Loading storage data…</div>
      ) : prefixBreakdown.length ? (
        <div
          aria-label="Root-prefix storage breakdown"
          className={styles.prefixList}
          tabIndex={0}
        >
          {prefixBreakdown.map((item) => (
            <div className={styles.prefixRow} key={item.prefix}>
              <span className={styles.prefixName}>
                {item.prefix}
                {registeredPrefixes.has(item.prefix) && (
                  <em
                    aria-label="Registered shared server"
                    className={styles.serverBadge}
                  >
                    server
                  </em>
                )}
              </span>
              <span className={styles.prefixObjects}>
                {item.object_count.toLocaleString()}{' '}
                {item.object_count === 1 ? 'object' : 'objects'}
              </span>
              <strong>{formatStorageBytes(item.total_size_bytes)}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.prefixMessage}>
          {breakdownAvailable
            ? 'No current objects found.'
            : 'Run Refresh to load the prefix breakdown.'}
        </div>
      )}
      {tooltipError && (
        <div className={styles.prefixError}>{tooltipError}</div>
      )}
      <div className={styles.prefixFooter}>
        Last scanned: {formatStorageDate(summary?.last_scanned_at)}
      </div>
    </div>
  );
  const prefixTooltipProps = {
    arrow: true,
    componentsProps: {
      tooltip: {
        sx: { bgcolor: 'transparent', maxWidth: 'none', p: 0 },
      },
    },
    describeChild: true,
    enterDelay: 300,
    placement: 'bottom-start' as const,
    title: prefixTooltip,
  };

  return (
    <>
      <Tooltip {...prefixTooltipProps}>
        <section
          aria-label="Server storage with root-prefix breakdown"
          className={`${styles.card} ${summary?.scan_error || loadError ? styles.failed : ''}`}
          tabIndex={0}
        >
          <div aria-hidden="true" className={styles.storageIcon}>
            <StorageOutlinedIcon />
          </div>
          <div className={styles.metric}>
            <div className={styles.metricLine}>
              <div
                aria-label="Show storage by root prefix"
                className={`${styles.metricValue} ${styles.metricTrigger}`}
              >
                <span>{prefixOnly ? 'Prefix S3' : 'All S3'}</span>
                <strong>
                  {loading
                    ? 'Loading…'
                    : formatStorageBytes(summary?.total_size_bytes)}
                </strong>
              </div>
              <div className={styles.metricValue}>
                <span>Displayed orgs</span>
                <strong>{loading ? '—' : formatStorageBytes(summary?.displayed_storage_bytes)}</strong>
              </div>
            </div>
            <small>{statusText}</small>
          </div>
          <button
            aria-label={scanning ? 'Scanning server S3 storage' : 'Refresh server S3 storage'}
            className={styles.refreshButton}
            disabled={loading || scanning || !!loadError || !summary?.server_prefix}
            onClick={() => setConfirmOpen(true)}
            type="button"
          >
            <RefreshIcon className={scanning ? styles.spinning : undefined} />
          </button>
        </section>
      </Tooltip>

      <Modal open={confirmOpen} onClose={requesting ? () => {} : () => setConfirmOpen(false)} loading={requesting}>
        <div className={styles.modalBody} onClick={(event) => event.stopPropagation()}>
          <h2>{prefixOnly ? 'Refresh server-prefix S3 storage?' : 'Refresh all S3 storage?'}</h2>
          <p>
            {prefixOnly
              ? 'Are you sure you want to scan this server’s S3 prefix and database references?'
              : 'Are you sure you want to scan every prefix in the shared S3 bucket and database references?'}
          </p>
          <code>
            {prefixOnly
              ? summary?.server_prefix
                ? `${summary.server_prefix}/`
                : 'Server prefix unavailable'
              : 'Entire shared private S3 bucket'}
          </code>
          <p className={styles.note}>
            {prefixOnly
              ? 'This scans current objects under this server prefix.'
              : 'This scans all current objects across every prefix in the bucket.'}{' '}
            It also writes this server&apos;s database-reference snapshot to the shared bucket.
            Repeat it on every registered server using the bucket. It will take some time.
          </p>
          <div className={styles.lastScanned}>
            Shared scan: {sharedScan?.servers_scanned || 0}/{sharedScan?.servers_total || 0} registered servers
          </div>
          <div className={styles.lastScanned}>Last scanned: {formatStorageDate(summary?.last_scanned_at)}</div>
          <div className={styles.modalActions}>
            <ContainedButton disabled={requesting} onClick={startScan}>
              Start scan
            </ContainedButton>
            <OutlinedButton disabled={requesting} onClick={() => setConfirmOpen(false)}>
              Cancel
            </OutlinedButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ServerS3StorageSummary;
